
import { useState, ChangeEvent } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useFileUpload } from './useFileUpload';
import { processDocument } from '@/services/embeddings';
import { useQueryClient } from '@tanstack/react-query';

interface UseKnowledgeEntryFormProps {
  onEntryCreated?: () => Promise<void> | void;
  onError?: (error: Error) => void;
  onProcessingStageChange?: (stage: string, progress: number) => void;
}

export function useKnowledgeEntryForm({ 
  onEntryCreated, 
  onError,
  onProcessingStageChange 
}: UseKnowledgeEntryFormProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { selectedFile, handleFileChange: handleFileSelect, uploadFile, clearSelectedFile } = useFileUpload();

  const resetForm = () => {
    setTitle('');
    setContent('');
    setTags('');
    setNotes('');
    clearSelectedFile();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    console.log('[DEBUG] Form submission started');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('[DEBUG] Auth user retrieved:', user?.id || 'No user');
      
      if (!user) {
        throw new Error('You must be logged in to create entries');
      }

      if (!selectedFile && (!content || content.trim() === '')) {
        throw new Error('Please enter some content or select a file to upload');
      }

      if (selectedFile) {
        console.log('[DEBUG] Selected file for upload:', {
          name: selectedFile.name, 
          size: selectedFile.size, 
          type: selectedFile.type
        });
        
        let fileUrl = null;
        try {
          if (onProcessingStageChange) onProcessingStageChange("uploading", 0);
          console.log('[DEBUG] Starting file upload...');
          
          fileUrl = await uploadFile();
          if (!fileUrl) {
            throw new Error('Failed to upload file');
          }
          console.log('[DEBUG] File uploaded successfully:', fileUrl);
          
          if (onProcessingStageChange) onProcessingStageChange("uploading", 100);
        } catch (error) {
          console.error('[DEBUG] File upload error:', error);
          throw new Error('Failed to upload file. Please try again.');
        }

        const processedTags = tags.split(',').map(tag => tag.trim()).filter(Boolean);
        console.log('[DEBUG] Processed tags:', processedTags);

        if (onProcessingStageChange) onProcessingStageChange("processing", 0);
        console.log('[DEBUG] Creating database entry...');

        const { data: entry, error: insertError } = await supabase
          .from('knowledge_entries')
          .insert({
            title: selectedFile.name,
            content: '',
            tags: processedTags,
            notes: notes.trim(),
            created_by: user.id,
            file_url: fileUrl,
            file_type: selectedFile.type,
            original_filename: selectedFile.name,
            metadata: {},
            embedding_status: 'pending'
          })
          .select()
          .single();

        if (insertError) {
          console.error('[DEBUG] Database insert error:', insertError);
          throw new Error('Failed to create entry in database');
        }

        if (!entry) {
          console.error('[DEBUG] No entry returned from database insert');
          throw new Error('No entry was created');
        }

        console.log('[DEBUG] Database entry created successfully:', entry);

        if (onProcessingStageChange) onProcessingStageChange("processing", 100);
        
        console.log('[DEBUG] Starting document processing with file URL:', fileUrl, 'and entry ID:', entry.id);
        const processingResult = await processDocument(fileUrl, entry.id);
        console.log('[DEBUG] Document processing result:', processingResult);
        
        if (onProcessingStageChange) onProcessingStageChange("extracting", 0);
        
        if (!processingResult.success) {
          console.error('[DEBUG] Document processing failed:', processingResult.error);
          toast({
            variant: "destructive",
            title: "Processing Error",
            description: "The entry was created but processing failed. An admin will review it.",
            duration: 5000
          });
        } else {
          console.log('[DEBUG] Document processed successfully');
          
          if (onProcessingStageChange) onProcessingStageChange("extracting", 100);
          
          if (onProcessingStageChange) onProcessingStageChange("analyzing", 50);
          
          console.log('[DEBUG] Invalidating and refetching queries...');
          await queryClient.invalidateQueries({ queryKey: ['knowledgeEntries'] });
          await new Promise(resolve => setTimeout(resolve, 1000));
          await queryClient.refetchQueries({ queryKey: ['knowledgeEntries'] });
          
          if (onProcessingStageChange) onProcessingStageChange("analyzing", 100);
          if (onProcessingStageChange) onProcessingStageChange("generating", 50);
          if (onProcessingStageChange) onProcessingStageChange("generating", 100);
          if (onProcessingStageChange) onProcessingStageChange("complete", 100);
          
          toast({
            title: "Entry Created",
            description: "Your knowledge entry has been created and processed",
            duration: 5000
          });
        }

        resetForm();
        if (onEntryCreated) {
          console.log('[DEBUG] Calling onEntryCreated callback...');
          await onEntryCreated();
        }
        return;
      }

      if (title.trim() === '') {
        throw new Error('Please enter a title');
      }

      const processedTags = tags.split(',').map(tag => tag.trim()).filter(Boolean);

      const { data: entry, error: insertError } = await supabase
        .from('knowledge_entries')
        .insert({
          title,
          content,
          tags: processedTags,
          notes: notes.trim(),
          created_by: user.id,
          embedding_status: 'pending'
        })
        .select()
        .single();

      if (insertError) {
        console.error('Insert error:', insertError);
        throw new Error('Failed to create entry in database');
      }

      toast({
        title: "Entry Created",
        description: "Your knowledge entry has been created",
        duration: 5000
      });

      resetForm();
      if (onEntryCreated) {
        await onEntryCreated();
      }
    } catch (error) {
      console.error('[DEBUG] Error creating knowledge entry:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create knowledge entry';
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
        duration: 5000
      });
      if (onError) {
        onError(error instanceof Error ? error : new Error(errorMessage));
      }
    } finally {
      console.log('[DEBUG] Form submission completed');
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (fileOrEvent: File | ChangeEvent<HTMLInputElement>) => {
    if (fileOrEvent instanceof File) {
      const acceptedFileTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/tiff'];
      
      let fileName = fileOrEvent.name;
      const fileNameParts = fileName.split('.');
      if (fileNameParts.length > 1) {
        const extension = fileNameParts.pop();
        fileName = `${fileNameParts.join('.')}.${extension.toLowerCase()}`;
      }
      
      const normalizedFile = new File([fileOrEvent], fileName, {
        type: fileOrEvent.type,
        lastModified: fileOrEvent.lastModified
      });
      
      if (acceptedFileTypes.includes(normalizedFile.type)) {
        handleFileSelect({
          target: {
            files: [normalizedFile]
          }
        } as unknown as ChangeEvent<HTMLInputElement>);
      } else {
        toast({
          variant: "destructive",
          title: "Invalid File",
          description: "Please select a supported file type (PDF, JPG, PNG, TIFF)",
        });
      }
    } else {
      handleFileSelect(fileOrEvent);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value);
  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => setTags(e.target.value);
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value);

  return {
    title,
    content,
    tags,
    notes,
    selectedFile,
    isSubmitting,
    handleSubmit,
    handleFileChange,
    handleTitleChange,
    handleContentChange,
    handleTagsChange,
    handleNotesChange,
    clearSelectedFile
  };
}
